<template>
  <aside class="sidebar" :class="{ 'sidebar--mobile-open': isMobileMenuOpen }">
    <!-- Desktop Sidebar -->
    <div class="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:top-16 lg:bottom-0 lg:bg-gray-50 lg:border-r lg:border-gray-200 lg:z-30">
      <div class="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
        <nav class="mt-5 flex-1 px-2 space-y-1">
          <SidebarItem
            v-for="item in navigationItems"
            :key="item.name"
            :to="item.to"
            :icon="item.icon"
            :name="item.name"
            :is-active="isActiveRoute(item.to)"
          />
        </nav>
        
        <!-- Logout button at bottom -->
        <div class="px-2 pb-4">
          <button
            class="w-full flex items-center px-2 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors group"
            @click="handleLogout"
          >
            <ArrowRightOnRectangleIcon class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
            Выйти
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Sidebar Overlay -->
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 z-40 lg:hidden"
      @click="closeMobileMenu"
    >
      <div class="fixed inset-0 bg-gray-600 bg-opacity-75" />
    </div>

    <!-- Mobile Sidebar -->
    <div
      class="fixed top-16 bottom-0 left-0 z-50 w-64 bg-white lg:hidden transform transition-transform duration-300 ease-in-out"
      :class="{ 'translate-x-0': isMobileMenuOpen, '-translate-x-full': !isMobileMenuOpen }"
    >
      <div class="flex items-center justify-between h-16 px-4 bg-gray-50 border-b border-gray-200">
        <span class="text-lg font-semibold text-gray-900">Личный кабинет</span>
        <button
          class="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          @click="closeMobileMenu"
        >
          <XMarkIcon class="h-6 w-6" />
        </button>
      </div>
      
      <div class="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
        <nav class="mt-5 flex-1 px-2 space-y-1">
          <SidebarItem
            v-for="item in navigationItems"
            :key="item.name"
            :to="item.to"
            :icon="item.icon"
            :name="item.name"
            :is-active="isActiveRoute(item.to)"
            @click="closeMobileMenu"
          />
        </nav>
        
        <!-- Logout button at bottom -->
        <div class="px-2 pb-4">
          <button
            class="w-full flex items-center px-2 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors group"
            @click="handleLogout"
          >
            <ArrowRightOnRectangleIcon class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
            Выйти
          </button>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { 
  ChartBarIcon,
  KeyIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon
} from '@heroicons/vue/24/outline'
import SidebarItem from './SidebarItem.vue'

// Props
interface Props {
  isMobileMenuOpen?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isMobileMenuOpen: false
})

// Emits
interface Emits {
  (e: 'close-mobile-menu'): void
}

const emit = defineEmits<Emits>()

// Composables
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

// Navigation items
const navigationItems = [
  {
    name: 'Обзор',
    to: '/account/dashboard',
    icon: ChartBarIcon,
  },
  {
    name: 'API-ключи',
    to: '/account/api-keys',
    icon: KeyIcon,
  },
  {
    name: 'Биллинг',
    to: '/account/billing',
    icon: CreditCardIcon,
  },
  {
    name: 'Настройки',
    to: '/account/settings',
    icon: Cog6ToothIcon,
  },
]

// Computed
const isActiveRoute = computed(() => (routePath: string) => {
  return route.path === routePath
})

// Methods
const handleLogout = async () => {
  await authStore.logout()
  await router.push('/')
  closeMobileMenu()
}

const closeMobileMenu = () => {
  emit('close-mobile-menu')
}
</script>

<style scoped>
.sidebar {
  /* Custom styles if needed */
}

.sidebar--mobile-open {
  /* Additional mobile styles if needed */
}
</style> 